import React, {useCallback, useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import CloseButton from '../CloseButton';
import StatusIcon from '../StatusIcon';

const Notification = React.forwardRef((props, ref) => {
  const {
    duration,
    onClose,
    type,
    title,
    closable,
    className,
    children,
    width,
    customIcon,
    triggerByToast,
    style,
    ...rest
  } = props;

  const [display, setDisplay] = useState('show');
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(duration > 0 ? 1 : 0);
  const timeoutRef = useRef(null);
  const remainingTimeRef = useRef(duration);
  const startTimeRef = useRef(Date.now());
  const endTimeRef = useRef(Date.now() + (duration || 0));

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (duration <= 0) return;

    clearTimer();
    startTimeRef.current = Date.now();
    endTimeRef.current = startTimeRef.current + remainingTimeRef.current;
    timeoutRef.current = setTimeout(() => {
      onClose?.();
    }, remainingTimeRef.current);
  }, [duration, onClose, clearTimer]);

  useEffect(() => {
    remainingTimeRef.current = duration;
    setProgress(duration > 0 ? 1 : 0);
  }, [duration]);

  useEffect(() => {
    if (duration > 0 && !isPaused) {
      startTimer();
    }

    return () => clearTimer();
  }, [duration, isPaused, startTimer, clearTimer]);

  useEffect(() => {
    if (duration <= 0) return;

    const update = () => {
      const now = Date.now();
      const remaining = isPaused
        ? remainingTimeRef.current
        : Math.max(0, endTimeRef.current - now);
      setProgress(duration > 0 ? remaining / duration : 0);
    };

    update();
    const intervalId = setInterval(update, 100);
    return () => clearInterval(intervalId);
  }, [duration, isPaused]);

  const handleMouseEnter = useCallback(() => {
    if (duration > 0) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(
        0,
        remainingTimeRef.current - elapsed
      );
      setIsPaused(true);
      clearTimer();
    }
  }, [duration, clearTimer]);

  const handleMouseLeave = useCallback(() => {
    if (duration > 0 && isPaused) {
      setIsPaused(false);
      startTimer();
    }
  }, [duration, isPaused, startTimer]);

  const handleClose = useCallback(
    e => {
      setDisplay('hiding');
      onClose?.(e);
      clearTimer();
      setProgress(0);
      if (!triggerByToast) {
        setTimeout(() => {
          setDisplay('hide');
        }, 400);
      }
    },
    [onClose, clearTimer, triggerByToast]
  );

  const notificationClass = classNames('notification', className);

  if (display === 'hide') {
    return null;
  }

  return (
    <div
      ref={ref}
      {...rest}
      className={notificationClass}
      style={{width: width, ...style}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={classNames('notification-content', !children && 'no-child')}
      >
        {type && !customIcon ? (
          <div className="mr-3">
            <StatusIcon type={type} />
          </div>
        ) : null}
        {customIcon && <div className="mr-3">{customIcon}</div>}
        <div className="mr-4">
          {title && (
            <div
              className={classNames('notification-title', children && 'mb-1')}
            >
              {title}
            </div>
          )}
          <div className="notification-description">
            {typeof children === 'function' ? children() : children}
          </div>
        </div>
      </div>
      {duration > 0 && (
        <div
          style={{
            height: 2,
            backgroundColor: 'rgba(0,0,0,0.08)'
          }}
        >
          <div
            style={{
              height: 2,
              width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
              transition: isPaused ? 'none' : 'width 100ms linear',
              backgroundColor:
                type === 'success'
                  ? '#10B981'
                  : type === 'warning'
                    ? '#F59E0B'
                    : type === 'danger'
                      ? '#EF4444'
                      : '#3B82F6'
            }}
          />
        </div>
      )}
      {closable && (
        <CloseButton
          className="notification-close"
          defaultStyle={false}
          absolute={true}
          onClick={handleClose}
        />
      )}
    </div>
  );
});

Notification.propTypes = {
  duration: PropTypes.number,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
  title: PropTypes.node,
  closable: PropTypes.bool,
  width: PropTypes.number,
  customIcon: PropTypes.node
};

Notification.defaultProps = {
  duration: 3000,
  width: 350,
  closable: false,
  triggerByToast: false
};

export default Notification;
